// The founder-facing money view (Slice 6; slice-plan.md:83-92; workflows.md:232-241; ADR-0023).
//
// D27 IS STRUCTURAL HERE (ADR-0023 decision 2): the founder sees plan capacity (value units) and
// an all-in cost broken down ONLY by `BillingCategory` (cloud/services/nightcrew_plan) - never a
// token, a per-fix, or a per-action figure (00-architecture invariant 8; BUILD-SPEC.md:25). Every
// customer-facing schema below is `z.strictObject` so a smuggled `perFixCost`/`perActionCost`/
// `tokensIn`/`costMicroEur`/`model` key is a PARSE ERROR, never a silently-stripped field -
// mirroring the `BuildStepView` precedent (model/build.ts:121-134) and the `AppView` precedent
// (model/apps.ts:62-87). This module is DERIVED and never stored (no `money_snapshots` table,
// ADR-0023 decision 3): it composes `UsageCounter` (billing.ts:36-51, "no cost field by
// construction") with the billing inbox into a read-only projection. THE ADMIN COST LEDGER
// (`MicroEur`, money.ts:21-27; `run-ledger.ts`) IS A SEPARATE, ADMIN-ONLY SURFACE - this module
// imports only `MinorUnits` (customer minor units) and plain integers, and MUST NEVER import
// `MicroEur` (ADR-0023 decision 2b).
import { z } from "zod";
import { AccountId, AppId, BillingInboxItemId } from "../ids.js";
import { CurrencyCode, MinorUnits } from "../money.js";
import { NonEmptyString, Timestamp } from "./common.js";
import { BillingItemKind, BillingSource, CeilingSource } from "./enums.js";
/**
 * DERIVED plan-capacity projection (Slice 7 D48/D98; 00-architecture invariant 8). It carries
 * `UsageCounter`'s stored counts (billing.ts:60-77: value units used/included, overage, absorbed),
 * the monthly spend ceiling ("the one dollar figure customers see", D48) - a PLAN-LEVEL budget cap,
 * not a per-action cost, so it is D27-safe alongside the count fields - and ONE derived count that
 * has no stored column, `billedExtraChanges`. That last one is deliberately not a mirror: the
 * charge is capped at what a tier above would have cost (D98 clause 4) and no `usage_counters`
 * column holds the capped count, so the surface that has to state a charge cannot get it from the
 * counter alone. `ceilingSource` reuses the existing `apps.ceiling_source` enum (enums.ts:46;
 * auto|founder) rather than redefining it. NO per-fix/token/cost-per-action field exists here BY
 * CONSTRUCTION - `strictObject` makes a smuggled `costMinor`/`tokensIn`/`perFixCost` key a parse
 * error, not a silent strip.
 */
export const PlanCapacity = z.strictObject({
    valueUnitsUsed: z.int().nonnegative(),
    valueUnitsIncluded: z.int().nonnegative(),
    /**
     * The RAW excess: changes done past the allowance, exactly what `usage_counters.overage_units`
     * holds (usage-counters.ts:26). The honest count of WORK past the allowance, and it is kept
     * because it is the only field that says how big the month actually was.
     *
     * IT IS NOT THE COUNT THE FOUNDER IS CHARGED FOR, and it must never be rendered beside
     * `overagePriceMinor`. Billing is capped at what a tier above would have cost for the same work
     * (D98 clause 4, `computeMonthlyBill` in packages/money/src/allowance.ts:521), so past the
     * crossover this count and the invoice diverge: 45 changes on Solo is 33 here and 12 on the
     * invoice. Multiplying THIS by the rate quotes a charge the founder will not get.
     * `billedExtraChanges` below is the one whose product is the charge.
     */
    overageUnits: z.int().nonnegative(),
    /**
     * THE COUNT THE FOUNDER IS ACTUALLY CHARGED FOR, and the only one a customer surface may show
     * next to `overagePriceMinor` (§0 rule 4). `MonthlyBill.billedExtraChanges`
     * (packages/money/src/allowance.ts:441), which holds the identity
     *
     *     billedMinor === plan.priceMinor + billedExtraChanges * plan.overagePriceMinor
     *
     * by construction, so `count x rate` on the gauge line and on the invoice are the same
     * arithmetic. Equal to `overageUnits` while the upgrade cap is slack, strictly lower once it
     * binds.
     *
     * D27-ADMISSIBLE FOR THE SAME REASON `overageUnits` IS: it is a COUNT of changes, not a cost, not
     * a token count and not a margin. The price it multiplies (`overagePriceMinor`) is a price the
     * customer pays, which this surface already carries. Nothing here exposes what the work cost to
     * produce - that stays in `plan_cost_policy`, which the app role cannot reach at all. Two count
     * fields, two meanings, and `strictObject` is what keeps a third one from arriving unannounced.
     */
    billedExtraChanges: z.int().nonnegative(),
    /**
     * WHAT THE MONTH CHARGES FOR NIGHTCREW, all in, in minor units: the right-hand side of the identity
     * above, `MonthlyBill.billedMinor` (packages/money/src/allowance.ts:441). Zero means the charge
     * could NOT be resolved (no subscription row, or a plan ladder that could not be read whole) and a
     * surface must read it as "nothing to state" and abstain, exactly as it does for
     * `overagePriceMinor` - a real month always charges at least the plan price, so zero is never a
     * genuine total.
     *
     * IT EXISTS BECAUSE A CAPPED MONTH CANNOT BE STATED AS A RATE (ADR-0031). Once D98 clause 4 binds,
     * `overageUnits` and `billedExtraChanges` diverge, and a per-change price beside either one
     * multiplies out to a figure the founder will not be sent. A total has no second reading, so the
     * capped caption states this and quotes no rate at all.
     *
     * D27-ADMISSIBLE, AND FOR A STRONGER REASON THAN THE COUNTS: it is a PRICE the customer is charged,
     * and `nightcrew_plan` is already one of the three sanctioned `BillingCategory` lines below, so the
     * plan charge is a figure this surface may carry by construction. It is a MONTH total for a FLAT
     * plan plus its extra changes, never a per-action decomposition - nothing here divides by a fix, an
     * action or a token.
     */
    billedMinor: MinorUnits,
    /**
     * WHAT THE UPGRADE CAP TOOK OFF THE BILL, in minor units: `MonthlyBill.capReliefMinor`. Zero means
     * NO CAP BOUND this month.
     *
     * IT EXISTS BECAUSE THE SURFACE MUST NOT INFER THAT A CAP BOUND, and a panel proved it had to. The
     * first build gated its capped-month caption on `billedExtraChanges !== overageUnits`, reasoning that
     * the two counts only diverge once D98 clause 4 binds. THAT IS FALSE. They also diverge when the
     * plan's own extra-change rate is ZERO, which the withdrawn rungs in `plans` carry as the explicit
     * not-set escape 0022's CHECK carves out (0022:45). Executed against the real `computeMonthlyBill`:
     * withdrawn `solo` 4999/100/0 at 140 changes returns
     * `rawMinor 4999, billedMinor 4999, cappedByRung null, capReliefMinor 0, billedExtraChanges 0`
     * beside an `overageUnits` of 40 - so the caption claimed "capped this month at $49.99, so you never
     * pay more than the next plan up" about a month where nothing capped and no relief was given. A
     * fabricated claim about the customer's own bill, which is a section 0 rule 4 defect.
     *
     * So the wire carries the fact instead of a proxy for it, and a surface that wants to say the cap
     * worked reads THIS. `> 0` is the only admissible test.
     *
     * D27-ADMISSIBLE: money the customer did NOT pay on a flat plan, which is a price-side figure like
     * `billedMinor` beside it. It is a MONTH total, never a per-action decomposition.
     */
    capReliefMinor: MinorUnits,
    /**
     * Changes the platform absorbed rather than charged for (D99): rework the crew caused, and the
     * overrun on a quote it wrote too small. A count, never a cost, so invariant 8 holds. Shown to the
     * founder as evidence, not hidden as a courtesy.
     */
    absorbedUnits: z.int().nonnegative(),
    /**
     * Price of one extra change on this plan, in the account's currency minor units (D98). A PRICE, not
     * a cost: the customer needs it to decide, and a price the customer cannot see is a bill they
     * cannot consent to. The internal cost that produced it lives in plan_cost_policy, which the app
     * role cannot reach at all.
     */
    overagePriceMinor: MinorUnits,
    /** The monthly per-app spend ceiling in minor units (D48); null when unset (mirrors `App.monthlyCeilingMinor`, apps.ts:44). */
    monthlyCeilingMinor: MinorUnits.nullable(),
    ceilingSource: CeilingSource,
    /**
     * How far through the billing period, in `[0,1]`, or null when it cannot be determined.
     *
     * ONLY the `gauge_near` caption reads this, and only to decide whether "Plenty of time left" is
     * true (ADR-0038). A null renders `gauge_healthy`, which asserts nothing about time, so an
     * unknown period fails to the weaker sentence rather than to a claim.
     *
     * D27-SAFE STRUCTURALLY, not by judgement: D27 bans cost, token and per-fix figures from a
     * customer-facing path, and an elapsed fraction is none of those. It is derived from the billing
     * period the founder already sees. This object stays `z.strictObject`, so a smuggled key remains
     * a parse error.
     */
    periodElapsedFraction: z.number().min(0).max(1).nullable(),
    /**
     * Whether the PROVIDER carries a first-month discount on this subscription (AC2).
     *
     * A BOOLEAN, NOT AN AMOUNT, and the absence of an amount is the decision. ADR-0032 settled the
     * identical question for VAT: Stripe is merchant of record (D74), Stripe issues the invoice, and
     * there is no arithmetic for Nightcrew to build. A discount is the same class of fact. Nothing in
     * this system knows the discount's SIZE, so a `discountMinor` here would be a fabricated money
     * figure - and the surface's correct response is not to state a smaller total but to stop claiming
     * its own total is what the founder pays.
     *
     * D27-SAFE: it is neither a cost, a token count nor a per-fix figure, and it carries no amount at
     * all. This object stays `z.strictObject`, so a smuggled `discountMinor` remains a parse error.
     */
    firstMonthDiscountByProvider: z.boolean(),
    /**
     * Whether the keep-or-change prompt should be shown for this app (D48, AC3 clause 3).
     *
     * A BOOLEAN THE SERVER DECIDES, not a set of inputs the client reasons over. "The first time it
     * matters" is a judgement about the month, the limit and whether this founder has already been
     * asked, and putting those three on the wire would let two surfaces answer the question
     * differently. One answer, computed where the rows are.
     *
     * FALSE ONCE ANSWERED, permanently, per app. `apps.limit_prompt_answered_at` is what makes the
     * sanctioned line "We will not ask again for this app" true rather than aspirational.
     */
    limitPromptPending: z.boolean(),
    /**
     * Whether a founding-partner rate produced the figures on this panel (D57).
     *
     * A BOOLEAN, NEVER THE TERMS. The panel needs to NAME the discount, not restate it: the rate and
     * the total are already here as the figures the founder actually pays, and putting "30% off" on
     * the wire would create a second statement of the same fact that can drift from the first.
     *
     * IT EXISTS BECAUSE APPLYING A DISCOUNT SILENTLY IS ITS OWN DEFECT. A verification panel measured
     * a founding partner's panel rendering $10.50 an extra change beside a published $12.50 in
     * `crew/config/plan-language.json`, with no sentence connecting them - the register said one
     * thing, the product charged another, and nothing on the screen explained why. Section 0 rule 4
     * governs the figure; this is what lets the copy account for it.
     */
    foundingRateApplied: z.boolean(),
});
/**
 * THE D27 CATEGORY WALL (ADR-0023 decision 1). The only three lines an all-in cost may ever break
 * down into: the founder's own cloud/infra, third-party services, and the flat Nightcrew plan
 * price. NEVER a fix, an action, or a token decomposition - `z.enum` closes the set so a smuggled
 * `per_fix`/`per_action`/`token` category is unparseable. Widen only through an ADR.
 */
export const BillingCategory = z.enum(["cloud", "services", "nightcrew_plan"]);
/** One rolled-up cost line for one `BillingCategory` (D27). Integer minor units only. */
export const AllInCostLine = z.strictObject({
    category: BillingCategory,
    amountMinor: MinorUnits,
});
/**
 * The all-in cost for one app, one period (slice-plan.md:88: "the money view shows all-in cost
 * ... no token or per-fix cost appears"). `byCategory` is CLOSED to `BillingCategory` by
 * construction; `sourcedFrom` discloses which billing sources fed this total (provenance,
 * ADR-0023 decision 4) without carrying any per-line raw data. `strictObject` throughout rejects a
 * smuggled `perFixCost`/`perActionCost`/`tokensIn`/`model` key as a parse error, not a strip.
 */
export const AllInCost = z.strictObject({
    period: NonEmptyString,
    currency: CurrencyCode,
    totalMinor: MinorUnits,
    byCategory: z.array(AllInCostLine),
    sourcedFrom: z.array(BillingSource),
});
/**
 * The `partial` P&L member (the triad's UX reconciliation: abstain/partial/full, §0 #4). A founder
 * with revenue known but cost not (or vice versa) sees the KNOWN component(s) WITHOUT a fabricated
 * net - `netMinor` is deliberately ABSENT from this member (strictObject rejects it outright): net
 * is withheld, never computed from a partial picture. The `.refine` requires at least one of
 * `revenueMinor`/`allInCostMinor` to be present - a partial with NEITHER known collapses to
 * `insufficient_data`, so the two abstain-adjacent states stay distinct (mirrors the
 * `VerdictMember` refine-as-union-member precedent, model/validate.ts:97-114).
 */
const PartialPnl = z
    .strictObject({
    kind: z.literal("partial"),
    revenueMinor: MinorUnits.optional(),
    allInCostMinor: MinorUnits.optional(),
    reason: NonEmptyString,
})
    .refine((v) => v.revenueMinor !== undefined || v.allInCostMinor !== undefined, {
    message: "a `partial` P&L requires at least one of revenueMinor/allInCostMinor - neither known is `insufficient_data`, not `partial` (§0 #4)",
    path: ["revenueMinor"],
});
/**
 * THE P&L ABSTAIN WALL (§0 #4; ADR-0023 decision 4; mirrors the cited-or-abstain precedent,
 * ports/knowledge.ts:123-135). A discriminated union on `kind`, three states: `computed` carries
 * the three figures ONLY when both revenue and all-in cost are known and sourced; `partial`
 * carries whichever of revenue/cost IS known with no fabricated net (`PartialPnl` above);
 * `insufficient_data` ABSTAINS with a reason when NEITHER is known, rather than fabricating a net
 * figure. `computed` cannot smuggle a `reason`, `partial` cannot smuggle a `netMinor`, and an
 * abstain cannot smuggle a number - the three are distinct strict members.
 */
export const Pnl = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("computed"),
        revenueMinor: MinorUnits,
        allInCostMinor: MinorUnits,
        netMinor: z.int(),
    }),
    PartialPnl,
    z.strictObject({
        kind: z.literal("insufficient_data"),
        reason: NonEmptyString,
    }),
]);
/**
 * THE MONEY VIEW (slice-plan.md:86-88; ADR-0023). Composes plan capacity, the all-in cost, and
 * the abstain-capable P&L for one app - the founder-facing read surface. DERIVED, never stored
 * (ADR-0023 decision 3). Drops `accountId` (the caller's tenant is implicit, mirroring `AppView`,
 * model/apps.ts:69). `strictObject` at every level makes a smuggled cost/token field a parse
 * error throughout the whole tree, not only at the top.
 */
export const MoneyView = z.strictObject({
    appId: AppId,
    planCapacity: PlanCapacity,
    allInCost: AllInCost,
    pnl: Pnl,
});
/**
 * One raw billing-inbox item (slice-plan.md:86 "the billing inbox"; ADR-0023 decision 6, a NEW
 * `billing_inbox_items` table): a cloud/services invoice or the Nightcrew plan charge, landed by
 * a connector, an upload, a manual entry, or a forwarded email (`BillingSource`, enums.ts, mirrors
 * `FeedbackSource`). `sourceRef` carries the provenance pointer (e.g. an invoice or import id);
 * null for a manual entry. Feeds `AllInCost` by rolling up into its category and period.
 *
 * Deliberately `z.strictObject` (D27, the load-bearing test): a smuggled `perFixCost`/
 * `perActionCost`/`tokensIn`/`costMicroEur`/`model` key on a raw inbox item is a PARSE ERROR, not
 * a silent strip - the D27 wall covers every customer-adjacent surface, not only the aggregated
 * `MoneyView`.
 */
export const BillingInboxItem = z.strictObject({
    id: BillingInboxItemId,
    accountId: AccountId,
    appId: AppId,
    source: BillingSource,
    kind: BillingItemKind,
    amountMinor: MinorUnits,
    currency: CurrencyCode,
    period: NonEmptyString,
    sourceRef: NonEmptyString.nullable(),
    createdAt: Timestamp,
});
//# sourceMappingURL=money-view.js.map